import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { formatINR, formatINRPrecise } from "../../lib/utils.fmt";
import {
  Users, Calendar, Truck, Wallet, TrendingUp, CheckCircle2, XCircle,
  AlertTriangle, Package, Box, ShoppingCart, Activity, Plus
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export default function AdminDashboard() {
  const { tenant } = useAuth();
  const bottlesOn = tenant?.bottle_tracking_enabled !== false;
  const [d, setD] = useState(null);
  const [pw, setPw] = useState(null);  // platform wallet
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.get("/api/admin/dashboard")
      .then(({ data }) => setD(data))
      .catch((e) => setError(friendlyMessage(e)));
    api.get("/api/admin/wallet").then(({ data }) => setPw(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (error) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load the dashboard" description={error}
        action={<button onClick={load} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }
  if (!d) return <LoadingState fullHeight />;

  return (
    <div className="space-y-8 page-enter" data-testid="admin-dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Control Room</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">Today at a glance</h1>
      </div>

      {/* Platform Wallet banner */}
      {pw && (
        <Link to="/admin/wallet" className="block" data-testid="platform-wallet-banner">
          <div className={`rounded-3xl p-5 text-white flex items-center gap-4 ${
            pw.blocked ? "bg-gradient-to-br from-brand-clay to-red-700"
            : pw.low_balance ? "bg-gradient-to-br from-brand-sand to-orange-600"
            : "bg-gradient-to-br from-brand-sky to-blue-700"
          }`}>
            <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
              <Wallet className="w-6 h-6"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider font-bold text-white/85 flex items-center gap-2">
                Platform OTP Wallet
                {pw.blocked && <span className="px-2 py-0.5 rounded-full bg-white/20 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> OTPs blocked</span>}
                {!pw.blocked && pw.low_balance && <span className="px-2 py-0.5 rounded-full bg-white/20">Low balance</span>}
              </div>
              <div className="font-heading text-3xl font-extrabold tracking-tight mt-0.5" data-testid="dashboard-wallet-amount">
                {formatINR(pw.wallet.balance)}
              </div>
              <div className="text-xs text-white/85 mt-0.5">
                ≈ {Math.max(0, Math.floor(pw.wallet.balance / pw.otp_charge))} OTPs left · {formatINRPrecise(pw.otp_charge)} per OTP
              </div>
            </div>
            <button className="rounded-2xl bg-white px-4 h-11 font-bold text-sm inline-flex items-center gap-1.5 shrink-0 text-brand-ink hover:opacity-95">
              <Plus className="w-4 h-4"/> Recharge
            </button>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Kpi icon={ShoppingCart} label="Today Orders" value={d.today_orders} color="text-brand-sky"/>
        <Kpi icon={Calendar} label="Active Subs" value={d.active_subscriptions} color="text-brand-leaf"/>
        <Kpi icon={Users} label="Customers" value={d.total_customers} color="text-brand-ink"/>
        <Kpi icon={Truck} label="Riders" value={d.total_delivery_boys} color="text-brand-clay"/>
        <Kpi icon={TrendingUp} label="Revenue Today" value={formatINR(d.revenue_today)} color="text-brand-leaf"/>
        <Kpi icon={Wallet} label="Wallet (Customers)" value={formatINR(d.total_wallet_balance)} color="text-brand-sky"/>
        <Kpi icon={Activity} label="Completion Rate" value={`${d.completion_rate}%`} color="text-brand-leaf"/>
        <Kpi icon={AlertTriangle} label="Low Wallet" value={d.low_wallet_customers} color="text-brand-sand"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">7-day Revenue</h3>
            <div className="text-xs text-neutral-500">Weekly revenue: <b className="text-brand-ink">{formatINR(d.revenue_week)}</b></div>
          </div>
          <div className="h-64" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.revenue_series}>
                <CartesianGrid stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(s) => s.slice(5)} stroke="#9ca3af" fontSize={11}/>
                <YAxis stroke="#9ca3af" fontSize={11}/>
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e5e7eb" }} />
                <Line type="monotone" dataKey="revenue" stroke="#3A86FF" strokeWidth={3} dot={{ r: 4, fill: "#3A86FF" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200 p-6 space-y-4">
          <h3 className="font-heading font-bold text-lg">Delivery Status</h3>
          <StatusBar Icon={CheckCircle2} label="Delivered" value={d.delivered_today} total={d.today_orders} color="bg-brand-leaf"/>
          <StatusBar Icon={Truck} label="Pending" value={d.pending_today} total={d.today_orders} color="bg-brand-sky"/>
          <StatusBar Icon={XCircle} label="Failed" value={d.failed_today} total={d.today_orders} color="bg-brand-clay"/>
          <div className="pt-3 border-t border-neutral-100 grid grid-cols-2 gap-3 text-sm">
            {bottlesOn ? (
              <>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Bottles In Use</div>
                  <div className="font-heading text-2xl font-bold">{d.bottles_in_use}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Inventory</div>
                  <div className="font-heading text-2xl font-bold">{d.bottles_inventory}</div>
                </div>
              </>
            ) : (
              <div className="col-span-2">
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Cancelled today</div>
                <div className="font-heading text-2xl font-bold">{d.cancelled_today || 0}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-3xl border border-neutral-200 p-4 sm:p-5" data-testid={`kpi-${label.toLowerCase().replace(/[ /]/g, '-')}`}>
      <div className="flex items-center justify-between gap-2">
        <Icon className={`w-5 h-5 shrink-0 ${color}`} />
        <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 text-right truncate">{label}</div>
      </div>
      <div className="font-heading text-2xl sm:text-3xl font-extrabold mt-3 tracking-tight truncate">{value}</div>
    </div>
  );
}

function StatusBar({ Icon, label, value, total, color }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="inline-flex items-center gap-1.5 font-semibold text-neutral-700"><Icon className="w-4 h-4" /> {label}</span>
        <span className="font-mono font-bold">{value}</span>
      </div>
      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
